/**
 * Measure Schedule Selectors
 *
 * Pure selector functions over MeasureScheduleItem[] (loaded from
 * `och.equip_config_measure_schedule` via the backend). These are the
 * single canonical way to read measure × role × quarter data on the
 * frontend. Callers MUST NOT hardcode thresholds, weights, or the
 * measure × quarter matrix — use these selectors instead so DB-only
 * changes flow through to the UI.
 *
 * Two frontend naming conventions exist, both handled here:
 *
 *   - "Chart form" — used by the single-provider performance charts
 *     (equip-utils.ts, ProgressDetails). SQL `CRCS` → `Colorectal`,
 *     `ACO_Visits` → `ACO`, `ED_SendOut` → `EDSendOut`.
 *     Use `toFrontendMeasureName` / `toSqlMeasureName`.
 *
 *   - "Grid form" — used by the leadership heatmap, the Ask-a-Question
 *     modal, and anything that reads `LeadershipProviderRow.measures`.
 *     Keeps the SQL outcome measure names verbatim (`CRCS`, `ACO_Visits`,
 *     `ED_SendOut`); only the two DB→UI renames are applied (`PIMS` →
 *     `PIMs`, `Quality_Audits` → `Quality_Audit`). Use `toGridMeasureName`.
 *
 * Role → bonus_eligible_type mapping (must match DB och.equip_config_measure_schedule):
 *   primary-care-apc       → 'PCP'
 *   primary-care-physician → 'PC Physician'
 *   psychiatry-apc         → 'Psych Med'
 *   psychiatry-physician   → 'Psychiatrist'
 *   palliative-care-apc    → 'Palliative'
 *   teletriage             → 'Teletriage'
 *
 * Leadership-API display-label mapping (SQL CASE output → bonus_eligible_type):
 *   'PC APC'               → 'PCP'
 *   'Psychiatry APC'       → 'Psych Med'
 *   'Psychiatry Physician' → 'Psychiatrist'
 *   'PC Physician', 'Palliative', 'Teletriage' pass through unchanged.
 *   Use `leadershipDisplayToBonusEligibleType` on anything returned by the
 *   `/equip/leadership/*` routes before filtering against the schedule.
 *
 * SYNC: Synced to equip_services/src/lib/ via update.sh
 */

import type { MeasureScheduleItem } from './equip-types'

// ---------------------------------------------------------------------------
// Role / measure-name translation
// ---------------------------------------------------------------------------

export type ScheduleRole =
  | 'primary-care-physician'
  | 'primary-care-apc'
  | 'psychiatry-physician'
  | 'psychiatry-apc'
  | 'palliative-care-apc'
  | 'teletriage'

export type ScheduleQuarter = 'Q1' | 'Q2' | 'Q3' | 'Q4'

const ROLE_TO_BET: Record<ScheduleRole, string> = {
  'primary-care-physician': 'PC Physician',
  'primary-care-apc': 'PCP',
  'psychiatry-physician': 'Psychiatrist',
  'psychiatry-apc': 'Psych Med',
  'palliative-care-apc': 'Palliative',
  teletriage: 'Teletriage',
}

const FRONTEND_TO_SQL_MEASURE: Record<string, string> = {
  Colorectal: 'CRCS',
  ACO: 'ACO_Visits',
  EDSendOut: 'ED_SendOut',
  'PHQ-9': 'PHQ9',
}

const SQL_TO_FRONTEND_MEASURE: Record<string, string> = {
  CRCS: 'Colorectal',
  ACO_Visits: 'ACO',
  ED_SendOut: 'EDSendOut',
}

/**
 * Two DB→UI renames applied everywhere the schedule flows into the UI.
 * The schedule table stores `PIMS` and `Quality_Audits`; the dashboard
 * emits `PIMs` and `Quality_Audit`. Outcome measures (AWV, A1C, BCS,
 * CRCS, PHQ9, HTN, ACP, ACO_Visits, ED_SendOut) pass through unchanged.
 */
const DB_TO_GRID_MEASURE_NAME: Record<string, string> = {
  PIMS: 'PIMs',
  Quality_Audits: 'Quality_Audit',
}

/**
 * Maps leadership-API `bonusEligibleType` display labels (from the backend
 * SQL CASE expressions) to the canonical `bonus_eligible_type` values used
 * in the schedule table.
 *
 * Backend produces: "PC APC", "PC Physician", "Psychiatry APC", "Psychiatry Physician"
 * Schedule uses:    'PCP',    'PC Physician',  'Psych Med',      'Psychiatrist'
 * Pass-throughs:    'Palliative', 'Teletriage' (already match the DB).
 */
const LEADERSHIP_DISPLAY_TO_BET: Record<string, string> = {
  'PC APC': 'PCP',
  'Psychiatry APC': 'Psych Med',
  'Psychiatry Physician': 'Psychiatrist',
}

export function roleToBonusEligibleType(role: ScheduleRole): string {
  return ROLE_TO_BET[role]
}

export function toSqlMeasureName(name: string): string {
  return FRONTEND_TO_SQL_MEASURE[name] ?? name
}

export function toFrontendMeasureName(name: string): string {
  return SQL_TO_FRONTEND_MEASURE[name] ?? name
}

/**
 * Translate a DB measure name to the grid/consistency-table form used by
 * leadership views and `LeadershipProviderRow.measures` keys. Only applies
 * the `PIMS` → `PIMs` and `Quality_Audits` → `Quality_Audit` renames;
 * outcome measures are returned verbatim.
 */
export function toGridMeasureName(name: string): string {
  return DB_TO_GRID_MEASURE_NAME[name] ?? name
}

/**
 * Normalize a leadership-API `bonusEligibleType` display label to the
 * canonical schedule-table `bonus_eligible_type`. Pass-through for values
 * that already match the DB ('PC Physician', 'Palliative', 'Teletriage').
 */
export function leadershipDisplayToBonusEligibleType(display: string): string {
  return LEADERSHIP_DISPLAY_TO_BET[display] ?? display
}

function quarterToInt(quarter: ScheduleQuarter): number {
  return Number(quarter.slice(1))
}

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

function filterRoleQuarter(
  schedule: MeasureScheduleItem[],
  role: ScheduleRole,
  quarter: ScheduleQuarter,
): MeasureScheduleItem[] {
  const bet = roleToBonusEligibleType(role)
  const q = quarterToInt(quarter)
  return schedule.filter(
    row => row.isActive && row.bonusEligibleType === bet && row.quarter === q,
  )
}

/**
 * All active outcome-measure names for the given role × quarter, in
 * frontend form (e.g. `Colorectal`, `ACO`, `EDSendOut`). Excludes core
 * measures (PIMs / Coding / Sig Stnds) — those live elsewhere.
 */
export function getRoleQuarterOutcomeMeasures(
  schedule: MeasureScheduleItem[],
  role: ScheduleRole,
  quarter: ScheduleQuarter,
): string[] {
  return filterRoleQuarter(schedule, role, quarter)
    .filter(row => row.measureCategory === 'Outcome')
    .map(row => toFrontendMeasureName(row.measureName))
}

/**
 * Build the set of all active measure names (outcome + consistency) in
 * grid form for a given `bonus_eligible_type` × quarter.
 *
 * Returns `null` when no schedule data is available (empty items, missing
 * inputs) so callers can fall back to unfiltered rendering instead of
 * dropping every row during the initial load.
 *
 * This is the selector to use from leadership components and anything
 * that reads `LeadershipProviderRow.measures` keyed on grid-form names.
 */
export function getActiveMeasureNames(
  schedule: MeasureScheduleItem[],
  bonusEligibleType: string | null,
  quarter: number | null,
): Set<string> | null {
  if (!bonusEligibleType || !quarter || schedule.length === 0) return null
  const names = new Set<string>()
  let typeKnown = false
  for (const row of schedule) {
    if (row.bonusEligibleType !== bonusEligibleType) continue
    typeKnown = true
    if (!row.isActive) continue
    if (row.quarter !== quarter) continue
    names.add(toGridMeasureName(row.measureName))
  }
  // Unknown type (e.g. ADCOE, APC Leadership) — fall back to the union of all
  // active measures for this quarter across all known types, so quarter-based
  // filtering still applies while the role-specific set is undefined.
  if (!typeKnown) {
    for (const row of schedule) {
      if (!row.isActive || row.quarter !== quarter) continue
      names.add(toGridMeasureName(row.measureName))
    }
  }
  return names
}

function findRow(
  schedule: MeasureScheduleItem[],
  role: ScheduleRole,
  measure: string,
  quarter: ScheduleQuarter,
): MeasureScheduleItem | undefined {
  const bet = roleToBonusEligibleType(role)
  const q = quarterToInt(quarter)
  const sqlName = toSqlMeasureName(measure)
  return schedule.find(
    row =>
      row.isActive &&
      row.bonusEligibleType === bet &&
      row.quarter === q &&
      row.measureName === sqlName,
  )
}

/**
 * Operational threshold (0-100) used for pass/fail. Returns undefined if
 * the measure is not in the schedule for that role × quarter.
 */
export function getMeasureThreshold(
  schedule: MeasureScheduleItem[],
  role: ScheduleRole,
  measure: string,
  quarter: ScheduleQuarter,
): number | undefined {
  const row = findRow(schedule, role, measure, quarter)
  return row ? row.threshold * 100 : undefined
}

/**
 * Display threshold (0-100) shown to clinicians. Operational threshold
 * plus grace (0.01). Returns undefined if the measure is not scheduled.
 */
export function getMeasureThresholdDisplay(
  schedule: MeasureScheduleItem[],
  role: ScheduleRole,
  measure: string,
  quarter: ScheduleQuarter,
): number | undefined {
  const row = findRow(schedule, role, measure, quarter)
  return row ? row.thresholdDisplay * 100 : undefined
}

/**
 * Weight percentage for the given measure in that role × quarter.
 * Returns undefined if not scheduled.
 */
export function getWeightPct(
  schedule: MeasureScheduleItem[],
  role: ScheduleRole,
  measure: string,
  quarter: ScheduleQuarter,
): number | undefined {
  const row = findRow(schedule, role, measure, quarter)
  return row?.weightPct
}

/**
 * Sum of outcome-measure weights for the role × quarter. Should equal 60
 * for a properly configured quarter.
 */
export function getTotalOutcomeWeightPct(
  schedule: MeasureScheduleItem[],
  role: ScheduleRole,
  quarter: ScheduleQuarter,
): number {
  return filterRoleQuarter(schedule, role, quarter)
    .filter(row => row.measureCategory === 'Outcome')
    .reduce((sum, row) => sum + row.weightPct, 0)
}

/**
 * True iff the role has exactly one outcome measure in the quarter
 * (single-measure risk window).
 */
export function hasSingleMeasureRisk(
  schedule: MeasureScheduleItem[],
  role: ScheduleRole,
  quarter: ScheduleQuarter,
): boolean {
  return getRoleQuarterOutcomeMeasures(schedule, role, quarter).length === 1
}

// ---------------------------------------------------------------------------
// Leadership grid helpers
// ---------------------------------------------------------------------------

/**
 * Canonical column ordering for the leadership heatmap grid, in grid form.
 */
export const LEADERSHIP_MEASURE_ORDER: readonly string[] = [
  'AWV', 'A1C', 'BCS', 'CRCS', 'PHQ9', 'HTN', 'ACP',
  'ACO_Visits', 'ED_SendOut', 'PIMs', 'Coding', 'Quality_Audit',
]

/**
 * Returns the measure columns relevant for a leadership heatmap, computed
 * as the union of active measures across all provider types in the set,
 * ordered by `LEADERSHIP_MEASURE_ORDER`.
 *
 * Handles backend display labels ("PC APC", "Psychiatry APC", etc.) via
 * `leadershipDisplayToBonusEligibleType`. Returns `null` when the schedule
 * hasn't loaded yet so callers can render an unfiltered fallback.
 */
export function getLeadershipMeasureColumnsFromSchedule(
  schedule: MeasureScheduleItem[],
  providers: { bonusEligibleType: string | null }[],
  quarter: number,
): string[] | null {
  if (schedule.length === 0) return null
  const rawTypes = Array.from(
    new Set(providers.map(p => p.bonusEligibleType).filter((t): t is string => !!t)),
  )
  const columnSet = new Set<string>()
  for (const raw of rawTypes) {
    const bet = leadershipDisplayToBonusEligibleType(raw)
    const active = getActiveMeasureNames(schedule, bet, quarter)
    if (active) active.forEach(m => columnSet.add(m))
  }
  return LEADERSHIP_MEASURE_ORDER.filter(m => columnSet.has(m))
}
